import { useEffect } from 'react';
import { Download, Share2, Smartphone } from 'lucide-react';

const QRCode = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // QR Code for the website - Generated using free QR code API
  const websiteURL = process.env.REACT_APP_WEBSITE_URL || window.location.origin;
  const qrCodeURL = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(websiteURL)}`;
  
  // Social Media QR Codes
  const socialQRCodes = [
    {
      platform: 'Instagram',
      handle: '@jfraifamilycatering',
      url: 'https://instagram.com/jfraifamilycatering',
      qrUrl: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent('https://instagram.com/jfraifamilycatering')}`,
      color: 'from-[#f09433] to-[#dc2743]'
    },
    {
      platform: 'Facebook',
      handle: '@jfraifamilycatering',
      url: 'https://facebook.com/jfraifamilycatering',
      qrUrl: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent('https://facebook.com/jfraifamilycatering')}`,
      color: 'from-[#1877f2] to-[#0d5dbf]'
    },
    {
      platform: 'TikTok',
      handle: '@jfraifamilycatering',
      url: 'https://tiktok.com/@jfraifamilycatering',
      qrUrl: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent('https://tiktok.com/@jfraifamilycatering')}`,
      color: 'from-[#00f2ea] to-[#ff0050]'
    }
  ];

  const downloadQR = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden" 
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-white drop-shadow-lg">QR Codes & Links</h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md">
            Scan to Connect with JFRAI Family
          </p>
        </div>
      </section>

      {/* Website QR Code */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-title">Website QR Code</h2>
          <div className="decorative-line"></div>
          <p className="text-lg text-[#6b4423] mb-8">
            Scan this code to visit our website instantly
          </p>
          
          <div className="card max-w-md mx-auto">
            <div className="bg-white p-8 rounded-lg inline-block mb-6">
              <img 
                src={qrCodeURL} 
                alt="JFRAI Family Catering Website QR Code"
                className="w-full max-w-[400px] mx-auto"
              />
            </div>
            <h3 className="text-xl font-bold text-[#8b4513] mb-2">JFRAI Family Catering</h3>
            <p className="text-[#6b4423] mb-4">{websiteURL}</p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => downloadQR(qrCodeURL, 'jfrai-website-qr.png')}
                className="btn-primary flex items-center gap-2"
              >
                <Download size={20} />
                Download QR Code
              </button>
              <button 
                onClick={() => navigator.share?.({ title: 'JFRAI Family Catering', url: websiteURL })}
                className="btn-secondary flex items-center gap-2"
              >
                <Share2 size={20} />
                Share
              </button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card bg-[#fff5e6]">
              <Smartphone className="text-[#d2691e] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#8b4513] mb-2">Print & Display</h4>
              <p className="text-sm text-[#6b4423]">Add to menus, flyers, business cards, and promotional materials</p>
            </div>
            <div className="card bg-[#fff5e6]">
              <Share2 className="text-[#d2691e] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#8b4513] mb-2">Easy Sharing</h4>
              <p className="text-sm text-[#6b4423]">Share digitally via email, text, or social media</p>
            </div>
            <div className="card bg-[#fff5e6]">
              <Download className="text-[#d2691e] mx-auto mb-3" size={32} />
              <h4 className="font-bold text-[#8b4513] mb-2">High Quality</h4>
              <p className="text-sm text-[#6b4423]">Download in high resolution for professional printing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media QR Codes */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title">Social Media QR Codes</h2>
          <div className="decorative-line"></div>
          <p className="text-center text-[#6b4423] mb-8">
            Scan to follow us on your favorite platform
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {socialQRCodes.map((social, index) => (
              <div key={index} className="card text-center">
                <div className={`h-2 w-full rounded-t-lg bg-gradient-to-r ${social.color} -mt-8 mb-4`}></div>
                <h3 className="text-2xl font-bold text-[#8b4513] mb-2">{social.platform}</h3>
                <p className="text-[#d2691e] font-semibold mb-4">{social.handle}</p>
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img 
                    src={social.qrUrl} 
                    alt={`${social.platform} QR Code`}
                    className="w-full max-w-[200px] mx-auto"
                  />
                </div>
                <button 
                  onClick={() => downloadQR(social.qrUrl, `jfrai-${social.platform.toLowerCase()}-qr.png`)}
                  className="btn-primary w-full text-sm"
                >
                  Download QR
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Tips */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="section-title">How to Use QR Codes</h2>
          <div className="decorative-line"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="card border-l-4 border-[#d2691e]">
              <h3 className="text-xl font-bold text-[#8b4513] mb-3">📱 For Marketing Materials</h3>
              <ul className="space-y-2 text-[#6b4423]">
                <li>• Business cards and letterhead</li>
                <li>• Menu cards and table tents</li>
                <li>• Promotional flyers and brochures</li>
                <li>• Event invitations</li>
                <li>• Vehicle wraps and signage</li>
              </ul>
            </div>

            <div className="card border-l-4 border-[#d2691e]">
              <h3 className="text-xl font-bold text-[#8b4513] mb-3">🎯 At Events</h3>
              <ul className="space-y-2 text-[#6b4423]">
                <li>• Display at catered events</li>
                <li>• Include in event programs</li>
                <li>• Add to photo booth props</li>
                <li>• Print on table cards</li>
                <li>• Share on event screens</li>
              </ul>
            </div>

            <div className="card border-l-4 border-[#d2691e]">
              <h3 className="text-xl font-bold text-[#8b4513] mb-3">💻 Digital Use</h3>
              <ul className="space-y-2 text-[#6b4423]">
                <li>• Email signatures</li>
                <li>• Social media posts and stories</li>
                <li>• Website footer or contact page</li>
                <li>• Digital business cards</li>
                <li>• Online advertisements</li>
              </ul>
            </div>

            <div className="card border-l-4 border-[#d2691e]">
              <h3 className="text-xl font-bold text-[#8b4513] mb-3">🏪 Physical Locations</h3>
              <ul className="space-y-2 text-[#6b4423]">
                <li>• Window displays and storefront</li>
                <li>• Partner venue displays</li>
                <li>• Trade show booths</li>
                <li>• Community bulletin boards</li>
                <li>• Local business partnerships</li>
              </ul>
            </div>
          </div>

          <div className="card bg-[#fff5e6] mt-8">
            <h3 className="text-xl font-bold text-[#8b4513] mb-3 text-center">💡 Pro Tip</h3>
            <p className="text-[#6b4423] text-center">
              Track QR code scans by using different codes for different marketing materials. 
              This helps you understand which channels drive the most traffic!
            </p>
          </div>
        </div>
      </section>

      {/* Download All */}
      <section className="section text-center" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
        <h2 className="section-title text-white">Ready to Use QR Codes?</h2>
        <div className="decorative-line bg-white"></div>
        <p className="section-subtitle text-white mb-8">
          Download all QR codes and start connecting with customers today!
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <button 
            onClick={() => downloadQR(qrCodeURL, 'jfrai-website-qr-large.png')}
            className="btn-primary bg-white text-[#f5576c] hover:bg-[#ffecd2]"
          >
            Download Website QR Code
          </button>
        </div>
      </section>
    </div>
  );
};

export default QRCode;
